import _ from "lodash";
import {
  generateRuleCondition,
  generateRuleExpression,
} from "components/TemplateEditor/Functions";
import { generateRules } from "functions/TemplateEditor";
import { DEFAULT_RULE } from "constants/TemplateEditor";

/**
 * @description Get rule condition by rule index
 * @param {any} rules - rules
 * @param {string} key - rule key
 * @param {Array<any>} dataTokens - data tokens
 */
export const getRuleCondition = (
  rules: Array<any>,
  key: any,
  dataTokens: Array<any>
): string => {
  try {
    const ruleIndex = getRuleIndex(key, rules);
    if (rules?.length > 0) {
      const ruleConditions = rules[ruleIndex]?.conditions?.map(
        (condition: any) => {
          return generateRuleCondition({
            ...condition,
            parameters: dataTokens,
          });
        }
      );
      return _.join(ruleConditions, " ");
    } else {
      return "";
    }
  } catch (error) {
    return "";
  }
};

/**
 * @description Get rule expression
 * @param {any} values - values
 * @param {Array<any>} dataTokens - data tokens
 */
export const getRuleExpression = (
  values: any,
  dataTokens: Array<any>
): Array<any> => {
  try {
    let { rules } = values ?? {};

    if (rules?.length > 0) {
      return rules?.map(({ conditions }: { conditions: Array<any> }) =>
        _.join(
          conditions?.map((condition: any) => {
            return generateRuleExpression({
              ...condition,
              parameters: dataTokens,
            });
          }),
          " "
        )?.trim()
      );
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

/**
 * @description Update section rules
 * @param {any} values - values
 * @param {any} template - template data
 * @param {Array<any>} dataTokens - data tokens
 * @param {any} defaultRule - default rule object
 * @param {any} section - current section data
 */
export const updateSectionRules = (
  values: any,
  template: any,
  dataTokens: Array<any>,
  defaultRule: any,
  section: any
): { updatedTemplate: any; rules: any } => {
  try {
    const templateContent = template.data?.templateContent ?? {};
    const { pageIndex, name } = section ?? {};

    let newRules: any = [];

    //Update rules array
    newRules = values?.rules?.map((ruleData: any) => {
      const ruleText: any = _.join(
        ruleData?.conditions?.map((condition: any) => {
          return generateRuleExpression({
            ...condition,
            parameters: dataTokens,
          });
        }),
        " "
      )?.trim();

      delete ruleData.id;
      delete ruleData.conditions;
      return {
        ...ruleData,
        rule: ruleText,
      };
    });

    //Update template data
    const findSectionIndex = templateContent?.pages[
      pageIndex
    ]?.sections?.findIndex((sectionData: any) => name == sectionData.name);

    let currentSection = _.find(templateContent?.pages[pageIndex]?.sections, {
      name: name,
    });

    // remove deleted rules from the template
    newRules = newRules.filter((rule: { isDeleted: any }) => !rule.isDeleted);

    currentSection = {
      ...currentSection,
      rules: [{ ...defaultRule }, ...newRules],
    };

    if (findSectionIndex >= 0 && currentSection) {
      templateContent.pages[pageIndex].sections[findSectionIndex] =
        currentSection;

      const updatedTemplate = {
        ...template.data,
        templateContent: {
          ...templateContent,
        },
      };

      return {
        updatedTemplate,
        rules: [defaultRule, ...newRules]?.map(({ rule, isDeleted }: any) => ({
          rule,
          isDeleted,
        })),
      };
    }

    return { updatedTemplate: undefined, rules: undefined };
  } catch (error) {
    return { updatedTemplate: undefined, rules: undefined };
  }
};

/**
 * @description Get initial rules
 * @param {any} templateContent - values
 * @param {any} section - template data
 */
export const getInitialRules = (
  templateContent: any,
  section: any
): { defaultRule: any; initialRules: Array<any> } => {
  try {
    const { pageIndex, name } = section ?? {};
    const currentSection = _.find(templateContent?.pages[pageIndex]?.sections, {
      name: name,
    });
    const defaultRule = _.find(currentSection?.rules, {
      rule: DEFAULT_RULE,
    });

    if (currentSection) {
      const initialRules = generateRules(
        currentSection?.rules
          ?.filter(({ rule }: any) => rule !== DEFAULT_RULE)
          ?.map((ruleData: any) => ({
            id: ruleData?.rule,
            ...ruleData,
          })),
        "section"
      );
      return { defaultRule, initialRules };
    } else {
      return { defaultRule: {}, initialRules: [] };
    }
  } catch (e) {
    return { defaultRule: {}, initialRules: [] };
  }
};

/**
 * @description Check whether the rules are duplicating
 * @param {any} rules - rules data
 * @param {any} dataTokens - data tokens
 */
export const isRulesDuplicating = (
  rules: Array<any>,
  dataTokens: any
): boolean => {
  try {
    const rulesData = rules?.map((rule: any) =>
      getRuleCondition(rules, rule?.key, dataTokens)
    );
    return _.uniq(rulesData).length !== rulesData.length;
  } catch (e) {
    return false;
  }
};

/**
 * @description Return rule index
 * @param key Rule key
 * @param rules Rules data
 */
export const getRuleIndex = (key: any, rules: Array<any>): number => {
  return rules?.findIndex((rule: any) => rule.key === key);
};
